//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Helmet } from 'react-helmet';
//------------------------------------------------------------------------------
// React DatePicker ------------------------------------------------------------
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import '@style/globals/_react_datepicker_overrides.scss';
import { StoreConfigContext } from '@helpers/storeConfigContext';

registerLocale('ja', ja);
registerLocale('ko', ko);
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import {
  AppBanner,
  Button,
  ButtonStyle,
  FilterSelector,
  SearchInput,
  PayoutButton,
  CommissionDetailButton,
} from '@cmp/common';
import { RecommendationHistory, Overview } from './components';
//------------------------------------------------------------------------------
// Assets ----------------------------------------------------------------------
import {
  searchIconUrl,
  OutBoundLinkIconBlue,
  DownloadIcon,
  CalendarIcon,
} from '@cmp/images';
//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import { withReferral } from '@helpers/referrals';
import { ReferralSortingOptions } from '@helpers/constants/referral';
import { withAuthUser } from '@helpers/auth';
import { loadProviders } from '@api/endpoints/get/providers';
import { withTranslation } from 'react-i18next';

//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class Recommendations extends React.Component {
  constructor(props) {
    super(props);
    const { t } = props;
    this.state = {
      searchValue: '',
      sortBy: ReferralSortingOptions(t).find((option) => option.default),
      startDate: null,
      endDate: null,
      shortSearchInput: true,
      providerData: {
        amzBalance: 0,
        balance: 0,
        purchaseCount: 0,
        recommendationCount: 0,
        totalEarnings: 0,
      },
    };
    this.onStartDateChange = this.onStartDateChange.bind(this);
    this.onEndDateChange = this.onEndDateChange.bind(this);
  }

  onStartDateChange(date) {
    this.setState({ startDate: date });
  }

  onEndDateChange(date) {
    this.setState({ endDate: date });
  }

  componentDidMount() {
    this.fetchProviders();
    const {
      storeConfig: { language },
    } = this.context;

    const normLanguage = language || navigator.language;
    this.setState({ language: normLanguage });
  }
  async fetchProviders() {
    const { startDate, endDate } = this.state;
    const shouldSendDate = endDate && startDate;
    const params = shouldSendDate
      ? {
          startDate: moment(startDate)
            .utc(true)
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .toISOString(),
          endDate: moment(endDate)
            .utc(true)
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .add(1, 'd')
            .toISOString(),
        }
      : null;
    const { data } = await loadProviders(params);
    this.setState({ providerData: data });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.startDate !== prevState.startDate ||
      this.state.endDate !== prevState.endDate
    ) {
      this.fetchProviders();
      this.historyRefetch();
    }
  }

  dashboardAuxiliarComponent() {
    const { startDate, endDate, language } = this.state;
    const { __referral, t } = this.props;

    const smallDevice = window.matchMedia('(max-width: 576px)').matches;
    const firstDayOfWeek = moment.localeData(language).firstDayOfWeek();
    const langBase = (language || 'en').split('-')[0].split('_')[0];
    const dateFormat =
      { ja: 'yyyy/MM/dd', ko: 'yyyy.MM.dd' }[langBase] || 'MM/dd/yyyy';

    const calendarInputIcon = (
      <CalendarIcon alt="" style={{ width: 16, height: 16 }} />
    );

    return (
      <div className={styles.AuxiliarComponent}>
        <div className={styles.DatePickers}>
          <FilterSelector
            title={t('dashboard_DateRange')}
            custom={
              <div className={styles.DatePickersWrapper}>
                <DatePicker
                  selected={startDate}
                  onChange={this.onStartDateChange}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={new Date()}
                  placeholderText={t('dashboard_DatePickerStart')}
                  calendarStartDay={firstDayOfWeek}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={10}
                  monthsShown={smallDevice ? 1 : 2}
                  locale={langBase !== 'en' ? langBase : undefined}
                  dateFormat={dateFormat}
                  showIcon
                  icon={calendarInputIcon}
                  isClearable
                />
                <DatePicker
                  selected={endDate}
                  onChange={this.onEndDateChange}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  placeholderText={t('dashboard_DatePickerEnd')}
                  calendarStartDay={firstDayOfWeek}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={10}
                  monthsShown={1}
                  locale={langBase !== 'en' ? langBase : undefined}
                  dateFormat={dateFormat}
                  showIcon
                  icon={calendarInputIcon}
                  isClearable
                />
              </div>
            }
          />
        </div>
        {!__referral ||
          !__referral.items ||
          (__referral.items.length <= 0 && (
            <Button
              style={ButtonStyle.Secondary}
              className={styles.CreateRecommendation}
              to="/"
            >
              {t('dashboard_CreateRecommendation')}
            </Button>
          ))}
      </div>
    );
  }

  historyAuxiliarComponent() {
    const { t, i18n } = this.props;
    const {
      startDate,
      endDate,
      sortBy,
      searchValue,
      providerData,
      shortSearchInput,
    } = this.state;
    const {
      featureFlags: { commissionDetailReport },
    } = this.context;

    const componentClasses = classNames(
      styles.AuxiliarComponent,
      styles.DashboardAuxiliarComponent
    );

    return (
      <div className={componentClasses}>
        <FilterSelector
          title={t('dashboard_SortBy')}
          options={ReferralSortingOptions(t)}
          value={sortBy}
          onChange={(sortBy) => this.setState({ sortBy }, this.historyRefetch)}
        />

        <SearchInput
          value={searchValue}
          placeholder={t('dashboard_SearchFor')}
          icon={searchIconUrl(i18n)}
          isShort={shortSearchInput && commissionDetailReport}
          onClickOpen={() => this.setState({ shortSearchInput: false })}
          onBlur={() => this.setState({ shortSearchInput: true })}
          onChange={(searchValue) => {
            this.setState({ searchValue }, () => {
              if (this.searchTimeout) clearTimeout(this.searchTimeout);
              this.searchTimeout = setTimeout(
                this.historyRefetch,
                searchValue.length > 0 ? 600 : 0
              );
            });
          }}
        />

        {commissionDetailReport && (
          <CommissionDetailButton
            className={styles.CommissionDetailButton}
            style={ButtonStyle.Primary}
            icon={DownloadIcon(i18n)}
            isShort={!shortSearchInput}
            startDate={startDate ? moment(startDate) : null}
            endDate={endDate ? moment(endDate) : null}
          />
        )}

        {providerData.stripeConnected && (
          <PayoutButton
            className={styles.PayoutButton}
            style={ButtonStyle.Outlined}
            icon={OutBoundLinkIconBlue(i18n)}
          />
        )}

        {/* <AmzPayout className={styles.AmzPayout} /> */}
      </div>
    );
  }

  headerComponent(title, auxiliar) {
    return (
      <div className={styles.Header}>
        {title}
        {auxiliar}
      </div>
    );
  }

  render() {
    const { searchValue, sortBy, startDate, endDate, providerData } =
      this.state;
    const {
      totalEarnings,
      recommendationCount,
      purchaseCount,
      balance,
      amzBalance,
    } = providerData;

    const { history, location } = this.props;
    const componentClasses = classNames(styles.All);

    const { t } = this.props;
    const AMZFeatureFlag = import.meta.env.VITE_AGCOD_PAYOUT_ENABLE === 'true';
    // Displaying both Amazon and Stripe regardless of payout selection and if AMZFeatureFlag is set to true.
    let showAmazon = AMZFeatureFlag;
    let showStripe = true;

    const {
      storeConfig: { language },
    } = this.context;

    return (
      <div className={componentClasses}>
        <AppBanner className={styles.RecommendationsAppBanner}></AppBanner>
        <Helmet>
          <title>{t('dashboard_Dashboard')}</title>
        </Helmet>

        <div className={styles.Section}>
          {this.headerComponent(
            <h2 className={styles.Title}>
              {t('dashboard_Dashboard')}
              {startDate && endDate
                ? (() => {
                    switch (language) {
                      case 'ja':
                        return ` ${moment(startDate).format(
                          'YYYY/MM/DD'
                        )} ～ ${moment(endDate).format('YYYY/MM/DD')}`;
                      case 'en':
                        return ` ${moment(startDate).format(
                          'MM/DD/YY'
                        )} To ${moment(endDate).format('MM/DD/YY')}`;
                      default:
                        return '';
                    }
                  })()
                : ''}
            </h2>,
            this.dashboardAuxiliarComponent()
          )}
          <Overview
            balance={balance}
            totalEarnings={totalEarnings}
            recommendationCount={recommendationCount}
            purchaseCount={purchaseCount}
          />
        </div>

        <div className={styles.Section}>
          {this.headerComponent(
            <h2 className={styles.Subtitle}>
              {t('dashboard_RecommendationHistory')}
            </h2>,
            this.historyAuxiliarComponent()
          )}
          <RecommendationHistory
            searchValue={searchValue}
            sortBy={sortBy.value}
            onChangeRefetch={(refetch) => (this.historyRefetch = refetch)}
            startDate={startDate ? moment(startDate) : null}
            endDate={endDate ? moment(endDate) : null}
            onClick={(id) => {
              history.push({
                pathname: `/recommendation/${id}`,
                state: { from: location, modal: true },
              });
            }}
          />
        </div>
      </div>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withAuthUser(withReferral(withTranslation()(Recommendations)));

Recommendations.contextType = StoreConfigContext;
